import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateInvoiceForwardingSettings, useUser } from '~/api/user';
import { AlertDialog, Button } from '~/components';
import { toast } from '~/utils/toast';

import type { DeleteAutoForwardingModalProps as Props } from './types';

export const DeleteAutoForwardingModal = ({ invoiceForwardingSetting, isDexxter, onClose, onDeleteSuccess }: Props) => {
  const { data: user } = useUser();
  const mutation = useUpdateInvoiceForwardingSettings();

  const { t } = useTranslation(['common', 'settings']);

  const onDelete = useCallback(() => {
    mutation.mutate(
      { entries: user.invoiceForwardingSettings.filter((entry) => entry.id !== invoiceForwardingSetting.id) },
      {
        onSuccess: () => {
          toast.success({ description: t('settings:alerts.integrations.successForwardingEmailUnlinked') });
          onDeleteSuccess();
          onClose();
        },
        onError: () => toast.danger({ description: t('common:error') }),
      },
    );
  }, [invoiceForwardingSetting.id, mutation, onClose, onDeleteSuccess, t, user.invoiceForwardingSettings]);

  return (
    <AlertDialog.Root onOpenChange={(open) => !open && onClose()} open>
      <AlertDialog.Body status="danger">
        <AlertDialog.Title>
          {isDexxter
            ? t('settings:integrations.forwarding.deleteModal.dexxter.title')
            : t('settings:integrations.forwarding.deleteModal.title')}
        </AlertDialog.Title>

        <AlertDialog.Description>
          {isDexxter
            ? t('settings:integrations.forwarding.deleteModal.dexxter.description')
            : t('settings:integrations.forwarding.deleteModal.description', { email: invoiceForwardingSetting.receiverEmail })}
        </AlertDialog.Description>

        <AlertDialog.Actions>
          <Button emphasis="status" icon="Trash" loading={mutation.isPending} onClick={onDelete}>
            {isDexxter
              ? t('settings:integrations.forwarding.deleteModal.dexxter.confirm')
              : t('settings:integrations.forwarding.deleteModal.confirm')}
          </Button>
        </AlertDialog.Actions>
      </AlertDialog.Body>
    </AlertDialog.Root>
  );
};
